import React, { useState } from "react";
import { Loader2, Award, ChevronDown } from "lucide-react";
import http from "@/api";

const DIMS = [
  { key: "elegance", ar: "الأناقة", en: "Elegance" },
  { key: "luxury", ar: "الفخامة", en: "Luxury" },
  { key: "romance", ar: "الرومانسية", en: "Romance" },
  { key: "occasion_fit", ar: "ملاءمة المناسبة", en: "Occasion fit" },
  { key: "value", ar: "القيمة مقابل السعر", en: "Value" },
];

const barColor = (v) => (v >= 80 ? "#15803d" : v >= 60 ? "#000000" : "#b45309");

export default function GiftScore({ productId, lang }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    if (data) { setOpen((o) => !o); return; }
    setOpen(true); setLoading(true); setErr("");
    try {
      const res = await http.post("/ai/gift-score", { product_id: productId, language: lang });
      setData(res.data);
    } catch {
      setErr(lang === "ar" ? "تعذّر التقييم — حاول لاحقاً" : "Couldn't rate — try later");
    } finally { setLoading(false); }
  };

  const overall = data?.overall || 0;
  const R = 26, C = 2 * Math.PI * R;

  return (
    <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="gift-score">
      <button onClick={load}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold hover:bg-black/[0.02]"
              data-testid="gift-score-toggle">
        <span className="flex items-center gap-2">
          <Award size={18} className="text-emerald-700" />
          {lang === "ar" ? "تقييم الهدية بالذكاء" : "AI Gift Score"}
        </span>
        {loading ? <Loader2 size={16} className="animate-spin" /> : <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 space-y-4" data-testid="gift-score-body">
          {err && <div className="text-sm text-red-600">{err}</div>}
          {loading && !data && (
            <div className="py-4 text-center text-sm opacity-60">{lang === "ar" ? "جارٍ التقييم..." : "Rating..."}</div>
          )}
          {data && (
            <>
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16 shrink-0">
                  <svg width="64" height="64" viewBox="0 0 64 64" className="-rotate-90">
                    <circle cx="32" cy="32" r={R} fill="none" stroke="var(--border)" strokeWidth="6" />
                    <circle cx="32" cy="32" r={R} fill="none" stroke={barColor(overall)} strokeWidth="6"
                            strokeLinecap="round" strokeDasharray={C} strokeDashoffset={C * (1 - overall / 100)} />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-lg font-bold leading-none" data-testid="gift-score-overall">{overall}</span>
                    <span className="text-[9px] opacity-60">/100</span>
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold mb-0.5">{lang === "ar" ? "التقييم العام" : "Overall"}</div>
                  <div className="text-xs opacity-75 leading-snug" data-testid="gift-score-verdict">{data.verdict}</div>
                </div>
              </div>

              <div className="space-y-2">
                {DIMS.map((d) => {
                  const v = data.scores?.[d.key] ?? 0;
                  return (
                    <div key={d.key} data-testid={`gift-score-dim-${d.key}`}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="opacity-80">{lang === "ar" ? d.ar : d.en}</span>
                        <span className="font-semibold">{v}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-black/[0.07] overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: barColor(v) }} />
                      </div>
                    </div>
                  );
                })}
              </div>

              {(data.best_for || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <span className="text-xs opacity-60">{lang === "ar" ? "الأنسب لـ:" : "Best for:"}</span>
                  {data.best_for.map((b, i) => (
                    <span key={i} className="chip !text-xs !py-0.5" data-testid={`gift-score-bestfor-${i}`}>{b}</span>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
